/**
 * Pure decision logic for the collection-loading strategies in
 * `AbstractLiveDataService`. No Angular, no HttpClient, no rxjs — so it
 * can be exercised by `tests/CollectionPaging.smoke.ts` under plain
 * node, which is the only way any of this has ever been observable
 * without signing in and watching a network tab.
 *
 * The protocol these helpers implement is documented in
 * `docs/contracts/shared/CollectionPaging.md`.
 */

/**
 * How many ids one limited-parallel chunk request asks for by default.
 *
 * Deliberately unrelated to the caller's page size. See
 * `idChunkSize()`.
 */
export const DEFAULT_ID_CHUNK_SIZE = 100;

/**
 * Size of one id chunk.
 *
 * The bug this exists to prevent: the chunk size used to BE the
 * caller's `limit`, and `startup.component.ts` loads PrintNodeOrders
 * with `limit` 10. The limited-parallel strategy already knows every id
 * it wants, so a small page size bought nothing and cost a request per
 * ten rows — a facility with 900 open orders started its session with
 * 90 requests that all looked identical
 * (`page=1&limit=10`, differing only inside the base64 filter).
 *
 * `limit` is still honoured as a floor so a caller that deliberately
 * asks for large pages is not chunked down to something smaller.
 *
 * Guards against a non-positive size, which would make the chunk loop
 * in `chunkIds()` never advance.
 */
export function idChunkSize(limit: number, defaultChunkSize: number = DEFAULT_ID_CHUNK_SIZE): number {
  const floor = Number.isFinite(limit) ? Math.floor(limit) : 0;
  const base = Number.isFinite(defaultChunkSize) ? Math.floor(defaultChunkSize) : DEFAULT_ID_CHUNK_SIZE;
  return Math.max(1, floor, base);
}

/**
 * Break an id list into chunks of at most `chunkSize`.
 *
 * A non-positive `chunkSize` is coerced to 1 rather than trusted: `for
 * (let i = 0; i < ids.length; i += 0)` is an infinite loop that builds
 * an ever-growing array of identical chunks, i.e. it hangs the tab
 * before a single request is made.
 */
export function chunkIds(ids: any[], chunkSize: number): any[][] {
  const size = Number.isFinite(chunkSize) && chunkSize >= 1 ? Math.floor(chunkSize) : 1;
  const chunks: any[][] = [];
  if (!ids || !ids.length) {
    return chunks;
  }
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size));
  }
  return chunks;
}

/**
 * Identity of a collection request. Two calls that produce the same
 * signature would put identical bytes on the wire, so the second can
 * join the first instead of repeating it.
 *
 * The role is part of it because it travels in the `selectedrole`
 * header and scopes the result — the same filter under a different role
 * is a different request, and must never be suppressed as a duplicate.
 */
export function collectionRequestSignature(
  shape: string,
  endPoint: string,
  selectedRole: string,
  selectedRoleId: string,
  limit: number,
  filter: any
): string {
  return [
    shape,
    endPoint,
    `${selectedRole}-${selectedRoleId}`,
    limit,
    filter ? JSON.stringify(filter) : ''
  ].join('|');
}

/**
 * The raw-row property that carries the primary key, derived from a
 * service's `objectQualifiedPrimaryKeyId()`.
 *
 * The qualified form is what the `in` operator needs
 * (`'package.package_ID'`); the rows that come back are keyed by the
 * bare column (`package_ID`). Deriving one from the other keeps a
 * service from having to declare the same fact twice.
 *
 * This is not cosmetic. `unrequestedIds()` used to read `item.ID`
 * unconditionally, and `Package` has no `ID` property at all — its key
 * is `package_ID`. So every row of every Package chunk looked like a
 * row nobody asked for, the violation guard tripped on the FIRST chunk,
 * and all remaining chunks were abandoned: a facility loaded at most
 * one chunk of packages and the Packages board came up empty. Reported
 * 2026-10-28.
 */
export function idPropertyFromQualifiedKey(qualifiedKey: string): string {
  if (!qualifiedKey) {
    return 'ID';
  }
  const dot = qualifiedKey.lastIndexOf('.');
  const property = dot === -1 ? qualifiedKey : qualifiedKey.substring(dot + 1);
  return property ? property : 'ID';
}

/**
 * The id carried by one raw row, as a string.
 *
 * Falls back to `ID` when the declared property is absent, because a
 * service that has not overridden `objectQualifiedPrimaryKeyId()` still
 * has to work, and because a server that answers with the bare key on
 * some routes and the qualified one on others should not silently
 * produce "no id".
 */
export function rowId(item: any, idProperty: string = 'ID'): string {
  if (!item) {
    return '' + undefined;
  }
  if (item[idProperty] !== undefined && item[idProperty] !== null) {
    return '' + item[idProperty];
  }
  return '' + item.ID;
}

/**
 * The ids in `items` that were NOT among `requestedIds`.
 *
 * Empty means the server honoured `in[<qualified pk>]`. Non-empty means
 * it did not, and every remaining chunk request would come back with
 * that same page — the visible symptom being the same handful of rows
 * requested over and over. The caller uses this to abandon the rest of
 * the chunks and say so, instead of completing the storm and reporting
 * success with an incomplete collection.
 *
 * `idProperty` MUST be the entity's real key property — pass
 * `idPropertyFromQualifiedKey(objectQualifiedPrimaryKeyId())`. Getting
 * it wrong does not under-report, it reports EVERY row as unrequested,
 * which reads as "the server ignored the filter" and abandons the load.
 *
 * Compared as strings because ids arrive as numbers from `page=-2` and
 * can arrive as numeric strings on a row.
 */
export function unrequestedIds(items: any[], requestedIds: any[], idProperty: string = 'ID'): string[] {
  if (!items || !items.length) {
    return [];
  }
  const requested = {};
  for (const id of (requestedIds || [])) {
    requested['' + id] = true;
  }
  const unexpected: string[] = [];
  for (const item of items) {
    const id = rowId(item, idProperty);
    if (!requested[id]) {
      unexpected.push(id);
    }
  }
  return unexpected;
}

